import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Flag, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { ChampionshipDetail, ChampionshipMatch, ChampionshipMatchStatus, ChampionshipTeam, PaginatedResult, Referee } from "@/types";
import { MATCH_STATUS_LABEL, apiError, championshipTone, formatDateTime, localInputToIso, matchLabel, personName, teamLabel } from "@/lib/championships";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, Badge } from "@/components/ui/Card";
import { TableSkeleton } from "@/components/ui/Skeleton";
import { Pagination } from "@/components/ui/Pagination";
import { ExportExcelButton } from "@/components/ui/ExportExcelButton";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { SearchSelect } from "@/components/ui/SearchSelect";
import { SortHeader, nextSort, type SortState } from "@/components/ui/SortHeader";
import { MatchFormModal } from "@/components/championships/MatchFormModal";

type SortKey = "fase" | "enfrentamiento" | "fecha" | "pista" | "arbitro" | "estado";

const SIN_FASE = "__sin_fase__";

const selectClass =
  "w-full rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

// Partidos del campeonato: listado paginado en servidor con filtros (texto, fase, estado, pareja,
// árbitro, fechas), orden por columna y Excel del conjunto filtrado. En pantallas estrechas las
// columnas secundarias pasan a una línea bajo el enfrentamiento en vez de obligar a hacer scroll.
export function MatchesSection({ campeonato }: { campeonato: ChampionshipDetail }) {
  const queryClient = useQueryClient();
  const id = campeonato.id;
  const canManage = campeonato.canManage;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim());
  const [fase, setFase] = useState("");
  const [estado, setEstado] = useState<"" | ChampionshipMatchStatus>("");
  const [equipo, setEquipo] = useState<ChampionshipTeam | null>(null);
  const [arbitro, setArbitro] = useState<Referee | null>(null);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [sort, setSort] = useState<SortState<SortKey>>({ key: "fase", dir: "asc" });

  const [editing, setEditing] = useState<{ match: ChampionshipMatch | null } | null>(null);
  const [deleting, setDeleting] = useState<ChampionshipMatch | null>(null);

  // `desde`/`hasta` son fechas sin zona horaria: se convierten a ISO en el navegador.
  const filterParams = useMemo(() => {
    const params = new URLSearchParams({ sort: sort.key, dir: sort.dir });
    if (search) params.set("search", search);
    if (fase) params.set("fase", fase);
    if (estado) params.set("estado", estado);
    if (equipo) params.set("equipoId", String(equipo.id));
    if (arbitro) params.set("arbitroId", String(arbitro.id));
    const d = desde ? localInputToIso(`${desde}T00:00`) : null;
    if (d) params.set("desde", d);
    const h = hasta ? localInputToIso(`${hasta}T23:59`) : null;
    if (h) params.set("hasta", h);
    return params;
  }, [search, fase, estado, equipo, arbitro, desde, hasta, sort]);

  const { data, isLoading } = useQuery({
    queryKey: ["championship", id, "partidos", "list", page, limit, filterParams.toString()],
    queryFn: async () => {
      const params = new URLSearchParams(filterParams);
      params.set("page", String(page));
      params.set("limit", String(limit));
      return (await api.get<PaginatedResult<ChampionshipMatch>>(`/championships/${id}/partidos?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({
    mutationFn: (partidoId: number) => api.delete(`/championships/${id}/partidos/${partidoId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["championship", id] });
      queryClient.invalidateQueries({ queryKey: ["championships"] });
      toast.success("Partido eliminado");
    },
    onError: (error) => toast.error(apiError(error, "No se pudo eliminar el partido")),
  });

  function toggleSort(key: SortKey) {
    setSort((s) => nextSort(s, key));
    setPage(1);
  }
  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  const hasFilters = Boolean(searchInput || fase || estado || equipo || arbitro || desde || hasta);
  const rows = data?.data ?? [];

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-medium text-ink-600">
          <Flag className="h-4 w-4 text-brand-600" />
          Partidos ({campeonato.partidosCount})
        </p>
        {canManage && (
          <Button onClick={() => setEditing({ match: null })}>
            <Plus className="h-4 w-4" />
            Nuevo partido
          </Button>
        )}
      </div>

      {campeonato.partidosCount > 0 && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <div className="col-span-2 sm:col-span-1">
              <Input
                label="Buscar"
                placeholder="Pareja, pista, árbitro…"
                value={searchInput}
                onChange={(e) => resetPage(setSearchInput)(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink-600">Fase</label>
              <select value={fase} onChange={(e) => resetPage(setFase)(e.target.value)} className={selectClass}>
                <option value="">Todas</option>
                {campeonato.fases.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
                <option value={SIN_FASE}>Sin fase</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink-600">Estado</label>
              <select
                value={estado}
                onChange={(e) => resetPage(setEstado)(e.target.value as "" | ChampionshipMatchStatus)}
                className={selectClass}
              >
                <option value="">Todos</option>
                {(Object.keys(MATCH_STATUS_LABEL) as ChampionshipMatchStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {MATCH_STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            </div>
            <SearchSelect<ChampionshipTeam>
              label="Pareja"
              value={equipo}
              onChange={resetPage(setEquipo)}
              queryKey={["championship", id, "equipos", "picker"]}
              fetchOptions={async (s) =>
                (await api.get<PaginatedResult<ChampionshipTeam>>(`/championships/${id}/equipos`, { params: { search: s, limit: 25, sort: "pareja" } })).data.data
              }
              getKey={(t) => t.id}
              getLabel={teamLabel}
              placeholder="Todas"
              searchPlaceholder="Pareja o jugador…"
              clearable
            />
            {/* Los árbitros solo se pueden listar con permiso de gestión (alimentan la asignación) */}
            {canManage && (
              <SearchSelect<Referee>
                label="Árbitro"
                value={arbitro}
                onChange={resetPage(setArbitro)}
                queryKey={["championships", "referees"]}
                fetchOptions={async (s) => (await api.get<Referee[]>("/championships/referees", { params: { search: s } })).data}
                getKey={(r) => r.id}
                getLabel={personName}
                getHint={(r) => r.email}
                placeholder="Todos"
                searchPlaceholder="Nombre o email…"
                clearable
              />
            )}
            <Input label="Desde" type="date" value={desde} onChange={(e) => resetPage(setDesde)(e.target.value)} className="w-full" />
            <Input label="Hasta" type="date" value={hasta} onChange={(e) => resetPage(setHasta)(e.target.value)} className="w-full" />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            {hasFilters ? (
              <Button
                onClick={() => {
                  setSearchInput("");
                  setFase("");
                  setEstado("");
                  setEquipo(null);
                  setArbitro(null);
                  setDesde("");
                  setHasta("");
                  setPage(1);
                }}
              >
                Limpiar filtros
              </Button>
            ) : (
              <span />
            )}
            <ExportExcelButton url={`/championships/${id}/partidos/export.xlsx?${filterParams.toString()}`} filename="partidos-campeonato.xlsx" />
          </div>
        </>
      )}

      {isLoading ? (
        <TableSkeleton rows={3} cols={5} />
      ) : !rows.length ? (
        <p className="text-sm text-ink-400">
          {hasFilters ? "Ningún partido coincide con los filtros." : "Todavía no hay ningún partido programado."}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-left text-sm">
            <thead className="bg-ink-50/60 text-xs tracking-wide text-ink-400 uppercase">
              <tr>
                <th className="px-3 py-3 sm:px-4">
                  <SortHeader label="Enfrentamiento" active={sort.key === "enfrentamiento"} dir={sort.dir} onClick={() => toggleSort("enfrentamiento")} />
                </th>
                <th className="hidden px-3 py-3 xl:table-cell xl:w-28">
                  <SortHeader label="Fase" active={sort.key === "fase"} dir={sort.dir} onClick={() => toggleSort("fase")} />
                </th>
                <th className="hidden px-3 py-3 sm:table-cell sm:w-40">
                  <SortHeader label="Fecha" active={sort.key === "fecha"} dir={sort.dir} onClick={() => toggleSort("fecha")} />
                </th>
                <th className="hidden px-3 py-3 2xl:table-cell 2xl:w-48">
                  <SortHeader label="Pista" active={sort.key === "pista"} dir={sort.dir} onClick={() => toggleSort("pista")} />
                </th>
                <th className="hidden px-3 py-3 2xl:table-cell 2xl:w-40">
                  <SortHeader label="Árbitro" active={sort.key === "arbitro"} dir={sort.dir} onClick={() => toggleSort("arbitro")} />
                </th>
                <th className="hidden px-3 py-3 sm:table-cell sm:w-32">
                  <SortHeader label="Estado" active={sort.key === "estado"} dir={sort.dir} onClick={() => toggleSort("estado")} />
                </th>
                <th className="hidden px-2 py-3 sm:table-cell sm:w-24" />
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const { a, b } = matchLabel(m);
                const pista = m.court ? `${m.court.club.nombre} — ${m.court.name}` : "Pista por definir";
                const arb = m.arbitro ? personName(m.arbitro) : "Sin árbitro";
                return (
                  <tr
                    key={m.id}
                    onClick={canManage ? () => setEditing({ match: m }) : undefined}
                    className={`border-t border-ink-100 transition-colors hover:bg-ink-50/50 ${canManage ? "cursor-pointer" : ""}`}
                  >
                    <td className="px-3 py-3 sm:px-4">
                      {/* Una pareja por línea (como un marcador): los nombres largos se recortan, nunca descuadran la fila */}
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="line-clamp-2 font-medium break-words text-ink-800 sm:line-clamp-1" title={a}>
                            {a}
                          </p>
                          <p className="line-clamp-2 font-medium break-words text-ink-800 sm:line-clamp-1" title={b}>
                            <span className="mr-1 text-xs font-normal text-ink-300">vs</span>
                            {b}
                          </p>
                        </div>
                        {m.setsA != null && m.setsB != null && (
                          <span className="shrink-0 rounded-md bg-ink-50 px-1.5 py-0.5 text-xs font-bold whitespace-nowrap text-ink-700">
                            {m.setsA} – {m.setsB}
                          </span>
                        )}
                      </div>
                      {/* Lo que no cabe en columnas según el ancho, en una línea bajo el enfrentamiento */}
                      <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-ink-400">
                        <span className="sm:hidden">
                          <Badge tone={championshipTone(m.estado)}>{MATCH_STATUS_LABEL[m.estado]}</Badge>
                        </span>
                        <span className="xl:hidden">{m.fase ?? "Sin fase"} ·</span>
                        <span className="sm:hidden">{formatDateTime(m.startsAt)} ·</span>
                        <span className="min-w-0 2xl:hidden">
                          {pista} · {arb}
                        </span>
                      </p>
                    </td>
                    <td className="hidden truncate px-3 py-3 text-ink-500 xl:table-cell" title={m.fase ?? undefined}>
                      {m.fase ?? "—"}
                    </td>
                    <td className="hidden px-3 py-3 text-ink-500 sm:table-cell">{formatDateTime(m.startsAt)}</td>
                    <td className="hidden truncate px-3 py-3 text-ink-500 2xl:table-cell" title={pista}>
                      {pista}
                    </td>
                    <td className="hidden truncate px-3 py-3 text-ink-500 2xl:table-cell" title={arb}>
                      {arb}
                    </td>
                    <td className="hidden px-3 py-3 sm:table-cell">
                      <Badge tone={championshipTone(m.estado)}>{MATCH_STATUS_LABEL[m.estado]}</Badge>
                    </td>
                    {/* En móvil no hay columna de acciones: tocar la fila abre el partido (ahí está también "Eliminar") */}
                    <td className="hidden px-2 py-3 sm:table-cell" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {m.matchEnded && m.reservationId && (
                          <Link
                            to={`/partidos/${m.reservationId}`}
                            title="Ver repetición"
                            className="rounded-lg p-2 text-brand-600 transition-colors hover:bg-brand-50"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        )}
                        {canManage && (
                          <>
                            <Button variant="ghost" className="px-2" onClick={() => setEditing({ match: m })} title="Editar partido">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" className="px-2" onClick={() => setDeleting(m)} title="Eliminar partido">
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={data?.page ?? 1}
        totalPages={data?.totalPages ?? 1}
        total={data?.total ?? 0}
        onPageChange={setPage}
        limit={limit}
        onLimitChange={(l) => {
          setLimit(l);
          setPage(1);
        }}
      />

      {editing && <MatchFormModal campeonato={campeonato} match={editing.match} onOpenChange={(open) => !open && setEditing(null)} />}

      {deleting && (
        <ConfirmDialog
          open={!!deleting}
          onOpenChange={(open) => !open && setDeleting(null)}
          title="Eliminar partido"
          description="Se eliminará el partido y, si todavía no ha empezado, se liberará la pista reservada. ¿Seguro?"
          tone="danger"
          confirmLabel="Eliminar"
          onConfirm={() => remove.mutate(deleting.id)}
        />
      )}
    </Card>
  );
}
